import { Component, inject, Injector, input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Checkpoint } from '../../data/checkpoint.model';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { CheckListStrategy } from './strategies/check-list.strategy';
import { RequestError, Store } from '@softline/core';
import {
  IconComponent,
  InputComponent,
  MessageBarService,
  ModalStore,
  ResolvableModal,
  SOFTLINE_FEATURE_MODAL,
  translator,
  VarDirective,
} from '@softline/ui-core';
import { Ticket } from '../../data/ticket.model';
import { CheckpointState } from '../../data/checkpoint-state.model';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  SOFTLINE_FEATURE_TICKET_CHECKPOINT,
  SOFTLINE_FEATURE_TICKET_CHECKPOINT_STATE,
  TicketCheckpointStateStore,
  TicketCheckpointStore,
} from '../../ticket.shared';
import { handleRequestError } from '@softline/application';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';

import { CheckboxComponent } from '@softline/ui-core';
import { FilterPipe } from '@softline/ui-core';
import { FirstPipe } from '@softline/ui-core';
import { SelectComponent, SelectOptionDirective, SelectOptionHeaderDirective, SelectOptionSeparatorDirective } from '@softline/ui-core/combo-box';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-check-list-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SelectOptionHeaderDirective,
    SelectOptionSeparatorDirective,
    TranslatePipe,
    CheckboxComponent,
    FilterPipe,
    FirstPipe,
    SelectComponent,
    SelectOptionDirective,
    SelectOptionDirective,
    IconComponent,
    VarDirective,
    InputComponent,
  ],
  templateUrl: './check-list-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./check-list-dialog.component.css'],
})
export class CheckListDialogComponent extends ResolvableModal<any> implements OnInit {
  private store = inject(Store);
  private route = inject(ActivatedRoute);
  private messageBarService = inject(MessageBarService);
  private injector = inject(Injector);

  private t = translator();

  strategy = input.required<CheckListStrategy>();
  ticket = input.required<Ticket>();

  checkpoints$ = this.store
    .observe(SOFTLINE_FEATURE_TICKET_CHECKPOINT, TicketCheckpointStore.getters.all)
    .pipe(
      map((checkpoints) => {
        const id = this.ticket().id;
        return checkpoints.filter((checkpoint) => +checkpoint?.ticketId === +(id ?? -1));
      }),
      map((checkpoints) =>
        checkpoints.filter(
          (checkpoint) =>
            this.strategy().availableTypes.findIndex((o) => o === checkpoint.type) > -1
        )
      )
    );
  checkpointStates$!: Observable<CheckpointState[]>;

  openedModals$ = this.store
    .observe(SOFTLINE_FEATURE_MODAL, ModalStore.getters.items)
    .pipe(map((o) => o?.length));

  action?: 'create' | 'update' | 'delete';

  form: UntypedFormGroup = new UntypedFormGroup({
    id: new UntypedFormControl(undefined),
    type: new UntypedFormControl(undefined),
    name: new UntypedFormControl(undefined),
    note: new UntypedFormControl(undefined),
    error: new UntypedFormControl(undefined),
    ticketId: new UntypedFormControl(undefined),
  });

  ngOnInit(): void {
    this.checkpointStates$ = combineLatest([
      this.checkpoints$,
      this.store.observe(
        SOFTLINE_FEATURE_TICKET_CHECKPOINT_STATE,
        TicketCheckpointStateStore.getters.all
      ),
    ]).pipe(
      map(([checkpoints, states]) =>
        states.filter(
          (state) =>
            this.strategy().stage === state.type &&
            checkpoints.findIndex((checkpoint) => checkpoint.id === state.checkpointId) >
              -1
        )
      )
    );
  }

  edit(checkpoint: Checkpoint | undefined, action: 'create' | 'update' | 'delete'): void {
    this.action = action;
    if (checkpoint) {
      this.form.patchValue(checkpoint);
    } else {
      const defaultValue = this.strategy().getDefaultValue();
      this.form.reset(defaultValue);
    }

    if (action === 'create') {
      this.form.patchValue({ ticketId: this.ticket().id });
    }
  }

  async setDone(
    event: Event,
    checkpoint: Checkpoint,
    done: boolean,
    checkpointState?: CheckpointState
  ): Promise<void> {
    await (checkpointState
      ? this.toggleCheckpointState({ ...checkpointState, done })
      : this.createCheckpointState({
          type: this.strategy().stage,
          done,
          checkpointId: checkpoint.id,
        }));
  }

  cancelEdit(): void {
    this.action = undefined;
    this.form.reset();
  }

  async save(
    checkpoint: Checkpoint,
    action: 'create' | 'update' | 'delete'
  ): Promise<void> {
    switch (action) {
      case 'create':
        await this.create(checkpoint);
        break;
      case 'update':
        await this.update(checkpoint);
        break;
      case 'delete':
        await this.delete(checkpoint);
        break;
      default:
        break;
    }
    this.resolve({ checkpoint, action });
  }

  filterCheckPointState(
    checkpointState: CheckpointState,
    checkpointId?: number
  ): boolean {
    return checkpointState.checkpointId === checkpointId;
  }

  private async create(checkpoint: Checkpoint): Promise<void> {
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_TICKET_CHECKPOINT,
        TicketCheckpointStore.actions.create,
        { entity: checkpoint }
      );
      this.messageBarService.success(
        this.t('#TICKETS.DIALOGS.CHECK_LIST.CREATE.SUCCESS')
      );
    } catch (e) {
      if (e instanceof RequestError) {
        this.messageBarService.error(
          this.t('#TICKETS.DIALOGS.CHECK_LIST.CREATE.FAILURE')
        );
      } else {
        handleRequestError(e, this.injector, { show: true });
      }
    }
  }

  private async update(checkpoint: Checkpoint): Promise<void> {
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_TICKET_CHECKPOINT,
        TicketCheckpointStore.actions.update,
        { entity: checkpoint }
      );
      this.messageBarService.success(
        this.t('#TICKETS.DIALOGS.CHECK_LIST.UPDATE.SUCCESS')
      );
    } catch (e) {
      if (e instanceof RequestError) {
        this.messageBarService.error(
          this.t('#TICKETS.DIALOGS.CHECK_LIST.UPDATE.FAILURE')
        );
      } else {
        handleRequestError(e, this.injector, { show: true });
      }
    }
  }

  private async delete(checkpoint: Checkpoint): Promise<void> {
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_TICKET_CHECKPOINT,
        TicketCheckpointStore.actions.delete,
        { entity: checkpoint }
      );
      this.action = undefined;
      this.form.reset();
      this.messageBarService.success(
        this.t('#TICKETS.DIALOGS.CHECK_LIST.DELETE.SUCCESS')
      );
    } catch (e) {
      if (e instanceof RequestError) {
        this.messageBarService.error(
          this.t('#TICKETS.DIALOGS.CHECK_LIST.DELETE.FAILURE')
        );
      } else {
        handleRequestError(e, this.injector, { show: true });
      }
    }
  }

  async createCheckpointState(state: Partial<CheckpointState>): Promise<void> {
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_TICKET_CHECKPOINT_STATE,
        TicketCheckpointStateStore.actions.create,
        { entity: state }
      );
      this.messageBarService.success(
        this.t('#TICKETS.DIALOGS.CHECK_LIST.STATE.CREATE.SUCCESS')
      );
    } catch (e) {
      if (e instanceof RequestError) {
        this.messageBarService.error(
          this.t('#TICKETS.DIALOGS.CHECK_LIST.STATE.CREATE.FAILURE')
        );
      } else {
        handleRequestError(e, this.injector, { show: true });
      }
    }
  }

  async toggleCheckpointState(state: CheckpointState): Promise<void> {
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_TICKET_CHECKPOINT_STATE,
        TicketCheckpointStateStore.actions.update,
        { entity: state }
      );
      this.messageBarService.success(
        this.t('#TICKETS.DIALOGS.CHECK_LIST.STATE.UPDATE.SUCCESS')
      );
    } catch (e) {
      if (e instanceof RequestError) {
        this.messageBarService.error(
          this.t('#TICKETS.DIALOGS.CHECK_LIST.STATE.UPDATE.FAILURE')
        );
      } else {
        handleRequestError(e, this.injector, { show: true });
      }
    }
  }
}
